import { useState, useRef, useEffect } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { getAnonToken, incrementSubtitleCount, getSubtitleCount, getRatingSummary } from './firebase';
import RatingWidget from './RatingWidget';
import './App.css';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB

// ── reCAPTCHA v3 ─────────────────────────────────────────────────────────────
async function getRecaptchaToken(action = 'submit') {
  if (!RECAPTCHA_SITE_KEY) return '';
  return new Promise((resolve) => {
    if (!window.grecaptcha) return resolve('');
    window.grecaptcha.ready(() =>
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve)
    );
  });
}

// ── SRT / VTT helpers ─────────────────────────────────────────────────────────
function fmtTime(sec) {
  const h  = Math.floor(sec / 3600);
  const m  = Math.floor((sec % 3600) / 60);
  const s  = Math.floor(sec % 60);
  const ms = Math.round((sec % 1) * 1000);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')},${String(ms).padStart(3,'0')}`;
}

function toSRT(segments) {
  return segments.map((seg, i) =>
    `${i + 1}\n${fmtTime(seg.start)} --> ${fmtTime(seg.end)}\n${seg.text.trim()}`
  ).join('\n\n');
}

function toVTT(segments) {
  const lines = ['WEBVTT', ''];
  segments.forEach((seg, i) => {
    lines.push(`${i + 1}`);
    lines.push(`${fmtTime(seg.start).replace(',','.')} --> ${fmtTime(seg.end).replace(',','.')}`);
    lines.push(seg.text.trim());
    lines.push('');
  });
  return lines.join('\n');
}

// ── Upgrade CTA ───────────────────────────────────────────────────────────────
function UpgradeCTA({ reason }) {
  return (
    <div className="upgrade-cta">
      <span className="upgrade-icon">⚡</span>
      <div className="upgrade-text">
        <strong>
          {reason === 'duration'
            ? 'Your video is over 10 minutes.'
            : 'Want translation, karaoke subtitles, and no ads?'}
        </strong>
        <p>Get unlimited video length, 20+ language translation, karaoke-style captions, and more at AIVantage.</p>
      </div>
      <a href="https://aivantage.tools" target="_blank" rel="noopener noreferrer" className="cta-btn">
        Try AIVantage free →
      </a>
    </div>
  );
}

// ── Contact Form ──────────────────────────────────────────────────────────────
function ContactForm() {
  const [email,   setEmail]   = useState('');
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState(''); // '' | 'sending' | 'sent' | 'error'

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient: 'admin@aivantage.tools',
          subject:   'General Enquiry',
          body:      `From: ${email}\n\n${message}`,
          reply_to:  email,
          site_name: 'AddTranscripts',
        }),
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="card" aria-label="Contact us">
      <h2 className="section-title">Contact Us</h2>
      <p className="hint" style={{ marginBottom: 16 }}>Have a question or feedback? We read every message.</p>
      {status === 'sent' ? (
        <p style={{ color: '#16a34a', fontWeight: 600 }}>✅ Message sent — we'll get back to you soon.</p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="field">
            <label>Your email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
          </div>
          <div className="field">
            <label>Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Your question or feedback…"
              rows={4}
              required
              style={{ resize: 'vertical', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.9rem', fontFamily: 'inherit' }}
            />
          </div>
          {status === 'error' && <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>Something went wrong. Please try again.</p>}
          <button className="btn-primary" type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      )}
    </section>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [file, setFile]                         = useState(null);
  const [filePath, setFilePath]                 = useState('');
  const [sessionId]                             = useState(() => `free_${Date.now()}`);
  const [uploadPct, setUploadPct]               = useState(0);
  const [uploading, setUploading]               = useState(false);
  const [transcribing, setTranscribing]         = useState(false);
  const [segments, setSegments]                 = useState([]);
  const [burning, setBurning]                   = useState(false);
  const [burnedPath, setBurnedPath]             = useState('');
  const [burnedSize, setBurnedSize]             = useState(0);
  const [downloadPct, setDownloadPct]           = useState(0);
  const [downloading, setDownloading]           = useState(false);
  const [error, setError]                       = useState('');
  const [durError, setDurError]                 = useState(false);
  const [language, setLanguage]                 = useState('');
  const [subtitleColor, setSubtitleColor]       = useState('#ffffff');
  const [subtitleStyle, setSubtitleStyle]       = useState('classic');
  const [subtitlePosition, setSubtitlePosition] = useState('bottom');
  const [subtitleSize, setSubtitleSize]         = useState('medium');
  const [subCount,     setSubCount]             = useState(null);
  const [ratingSummary, setRatingSummary]       = useState(null);
  const [editingIdx,   setEditingIdx]           = useState(null);
  const [videoBlobUrl, setVideoBlobUrl]         = useState('');
  const [currentTime,  setCurrentTime]          = useState(0);
  const videoRef = useRef();
  const segmentsRef = useRef();

  useEffect(() => {
    getSubtitleCount().then(setSubCount);
    getRatingSummary().then(setRatingSummary);
  }, []);


  useEffect(() => {
    if (!segmentsRef.current) return;
    const active = segmentsRef.current.querySelector('.segment--active');
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentTime]);

  const activeSegIdx = segments.findIndex(s => currentTime >= s.start && currentTime < s.end);
  const getActiveWordIdx = (seg) => {
    if (!seg.words?.length) return -1;
    return seg.words.findIndex(w => currentTime >= w.start && currentTime < w.end);
  };

  const fileRef = useRef();

  async function handleFile(f) {
    setFile(f);
    setFilePath('');
    setSegments([]);
    setBurnedPath('');
    setError('');
    setDurError(false);
    setEditingIdx(null);
    if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
    setVideoBlobUrl(URL.createObjectURL(f));
    setUploading(true);
    setUploadPct(0);
    try {
      const token       = await getAnonToken();
      const totalChunks = Math.ceil(f.size / CHUNK_SIZE);
      const uploadId    = `up_${Date.now()}`;
      let finalPath     = '';

      for (let i = 0; i < totalChunks; i++) {
        const chunk = f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd    = new FormData();
        fd.append('video', chunk, f.name);
        fd.append('session_id', sessionId);
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', totalChunks);

        const res  = await fetch(`${API}/public/free-upload-chunk`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        if (data.complete) finalPath = data.file_path;
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }
      setFilePath(finalPath);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  async function transcribe() {
    if (!filePath) return;
    setTranscribing(true);
    setError('');
    setDurError(false);
    setSegments([]);
    setBurnedPath('');
    try {
      const [token, rcToken] = await Promise.all([getAnonToken(), getRecaptchaToken('transcribe')]);
      const fd = new FormData();
      fd.append('file_path', filePath);
      fd.append('session_id', sessionId);
      fd.append('recaptcha_token', rcToken);
      if (language) fd.append('language', language);

      const res  = await fetch(`${API}/public/free-transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'duration_exceeded') setDurError(true);
        throw new Error(data.message || data.error || 'Transcription failed');
      }
      setSegments(data.segments || []);
      incrementSubtitleCount();
    } catch (e) {
      setError(e.message);
    } finally {
      setTranscribing(false);
    }
  }

  async function burnSubtitles() {
    if (!filePath || !segments.length) return;
    setBurning(true);
    setBurnedPath('');
    setError('');
    try {
      const [token, rcToken] = await Promise.all([getAnonToken(), getRecaptchaToken('burn')]);
      const fd = new FormData();
      fd.append('file_path', filePath);
      fd.append('srt_content', toSRT(segments));
      fd.append('subtitle_color', subtitleColor);
      fd.append('subtitle_style', subtitleStyle);
      fd.append('subtitle_position', subtitlePosition);
      fd.append('subtitle_size', subtitleSize);
      fd.append('recaptcha_token', rcToken);

      const res  = await fetch(`${API}/public/free-burn`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Burn failed');
      setBurnedPath(data.file_path);
      setBurnedSize(data.file_size || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setBurning(false);
    }
  }

  async function downloadBurned() {
    if (!burnedPath) return;
    setDownloading(true);
    setDownloadPct(0);
    try {
      const token     = await getAnonToken();
      const DCHUNK    = 20 * 1024 * 1024;
      const totalSize = burnedSize || DCHUNK;
      const parts     = [];

      for (let start = 0; start < totalSize; start += DCHUNK) {
        const end = Math.min(start + DCHUNK - 1, totalSize - 1);
        const res = await fetch(
          `${API}/public/free-download-burned?file_path=${encodeURIComponent(burnedPath)}&start=${start}&end=${end}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Download failed');
        parts.push(await res.arrayBuffer());
        setDownloadPct(Math.round(((end + 1) / totalSize) * 100));
      }
      const blob = new Blob(parts, { type: 'video/mp4' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'video_with_subtitles.mp4'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  }

  function exportFile(type) {
    const map = {
      srt: { content: toSRT(segments),                                  mime: 'text/plain', ext: 'srt' },
      vtt: { content: toVTT(segments),                                  mime: 'text/vtt',   ext: 'vtt' },
      txt: { content: segments.map(s => s.text.trim()).join('\n'),      mime: 'text/plain', ext: 'txt' },
    };
    const { content, mime, ext } = map[type];
    const blob = new Blob([content], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `transcript.${ext}`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>100% Precise Free AI Subtitles — 90+ Languages | AddTranscripts</title>
        <meta name="description" content="No sign-up, no watermark. The most accurate free subtitle generator online. Burn-in captions or export SRT/VTT in 60 seconds. Supports 90+ languages." />
        <meta property="og:title" content="AddTranscripts — Free Subtitle Editor Online" />
        <meta property="og:description" content="Upload a video, get AI captions, edit, burn subtitles in. Free, no account needed." />
        <link rel="canonical" href="https://addtranscripts.com/" />
        {RECAPTCHA_SITE_KEY && (
          <script src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`} async />
        )}
      </Helmet>

      <div className="app">
        {/* Nav */}
        <nav className="nav">
          <a href="/" className="logo">✍ AddTranscripts</a>
          <a href="https://aivantage.tools" target="_blank" rel="noopener noreferrer" className="nav-cta">
            Try AIVantage — More AI Tools →
          </a>
        </nav>

        {/* Hero */}
        <header className="hero">
          <h1>Add Subtitles to Video — Free, No Sign-Up</h1>
          <p className="hero-sub"><span className="hero-free">100% Free</span> · Add Captions · Burn-in Subtitles · Generate SRT · Transcribe Video</p>
          <div className="trust-badges">
            <span>✓ TikTok</span>
            <span>✓ Instagram Reels</span>
            <span>✓ YouTube Shorts</span>
            <span>✓ Any video</span>
          </div>
        </header>

        {/* How it works strip */}
        <div className="how-strip-wrap">
          <h2 className="how-strip-title">Add subtitles in 4 easy steps</h2>
          <div className="how-strip">
            {[
              { icon: '🎬', step: 'Upload', desc: 'Drop your MP4 or MOV' },
              { icon: '🤖', step: 'AI Transcribes', desc: 'Captions in 60 seconds' },
              { icon: '✏️', step: 'Edit', desc: 'Fix any mistakes' },
              { icon: '⬇️', step: 'Download', desc: 'SRT, VTT or burned-in video' },
            ].map((s, i, arr) => (
              <>
                <div key={i} className="how-strip-step">
                  <span className="how-strip-icon">{s.icon}</span>
                  <strong>{s.step}</strong>
                  <span>{s.desc}</span>
                </div>
                {i < arr.length - 1 && <div className="how-strip-arrow">→</div>}
              </>
            ))}
          </div>
        </div>

        <div className="page-layout">
        <main className="main-content">


          {/* Step 1 */}
          <section className="card">
            <h2 className="step-title"><span className="step-num">1</span> Upload your video</h2>
            <p className="hint">MP4 or MOV · max 500 MB · max 10 minutes</p>
            <div
              className={`drop-zone${file ? ' has-file' : ''}`}
              onClick={() => fileRef.current.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            >
              {file
                ? <span className="file-name">📎 {file.name}</span>
                : <><span className="drop-icon" style={{ fontSize: '3rem' }}>🎬</span><strong style={{ fontSize: '1.05rem', color: '#7c3aed' }}>Drop your video here</strong><span style={{ fontSize: '0.85rem' }}>or click to browse · MP4, MOV · up to 500 MB</span></>
              }
            </div>
            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,audio/*" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files[0]; if (f) handleFile(f); }} />
            {uploading && (
              <div className="progress-wrap">
                <div className="progress-track"><div className="progress-bar" style={{ width: `${uploadPct}%` }} /></div>
                <span className="progress-label">Uploading… {uploadPct}%</span>
              </div>
            )}
            {filePath && !uploading && <p className="success-msg">✅ Upload complete</p>}
          </section>


          {/* Step 2 */}
          {filePath && (
            <section className="card">
              <h2 className="step-title"><span className="step-num">2</span> Generate transcript</h2>
              <div className="row gap">
                <div className="field">
                  <label>Language (optional)</label>
                  <select value={language} onChange={e => setLanguage(e.target.value)}>
                    <option value="">Auto-detect</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="hi">Hindi</option>
                    <option value="ta">Tamil</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="ar">Arabic</option>
                    <option value="pt">Portuguese</option>
                  </select>
                </div>
                <button className="btn-primary" onClick={transcribe} disabled={transcribing}>
                  {transcribing ? 'Transcribing…' : 'Generate Transcript'}
                </button>
              </div>
              {transcribing && (
                <div className="waiting-wrap">
                  <div className="spinner" />
                  <p className="waiting-msg">AI is transcribing your video — this takes 30–90 seconds…</p>
                </div>
              )}
            </section>
          )}

          {/* Errors */}
          {error && <div className="error-box">{error}</div>}
          {durError && <UpgradeCTA reason="duration" />}

          {/* Step 3 */}
          {segments.length > 0 && (
            <section className="card">
              <h2 className="step-title"><span className="step-num">3</span> Edit transcript</h2>
              <p className="hint">Click <strong>Edit</strong> on any segment to fix the text.</p>

              <div className="transcript-layout">
                {/* Video preview */}
                {videoBlobUrl && (
                  <div className="video-preview-wrap">
                    <video
                      ref={videoRef}
                      src={videoBlobUrl}
                      controls
                      className="video-preview"
                      onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                    />
                  </div>
                )}

                {/* Segments */}
                <div className="segments" ref={segmentsRef}>
                  {segments.map((seg, i) => {
                    const isActive = i === activeSegIdx;
                    const activeWordIdx = isActive ? getActiveWordIdx(seg) : -1;
                    return (
                      <div key={i} className={`segment${isActive ? ' segment--active' : ''}`}>
                        <div className="seg-header">
                          <span className="seg-time">{fmtTime(seg.start)} → {fmtTime(seg.end)}</span>
                          {editingIdx === i ? (
                            <button className="seg-btn seg-btn--save" onClick={() => setEditingIdx(null)}>Save</button>
                          ) : (
                            <button className="seg-btn seg-btn--edit" onClick={() => setEditingIdx(i)}>✏ Edit</button>
                          )}
                        </div>
                        {editingIdx === i ? (
                          <textarea
                            className="seg-text"
                            value={seg.text}
                            rows={2}
                            autoFocus
                            onChange={e => {
                              const updated = [...segments];
                              updated[i] = { ...seg, text: e.target.value };
                              setSegments(updated);
                            }}
                          />
                        ) : (
                          <p className="seg-text-display">
                            {seg.words?.length > 0 ? seg.words.map((w, wIdx) => (
                              <span
                                key={wIdx}
                                onClick={() => { if (videoRef.current) videoRef.current.currentTime = w.start; }}
                                className={`word${isActive && wIdx === activeWordIdx ? ' word--active' : ''}`}
                              >
                                {w.word}{' '}
                              </span>
                            )) : seg.text}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="export-row">
                <span className="export-label">Export as:</span>
                <button className="btn-outline" onClick={() => exportFile('srt')}>SRT</button>
                <button className="btn-outline" onClick={() => exportFile('vtt')}>VTT</button>
                <button className="btn-outline" onClick={() => exportFile('txt')}>TXT</button>
                <button className="btn-outline" onClick={() => navigator.clipboard.writeText(segments.map(s => s.text.trim()).join('\n'))}>
                  Copy text
                </button>
              </div>
            </section>
          )}

          {/* Step 4 */}
          {segments.length > 0 && (
            <section className="card">
              <h2 className="step-title"><span className="step-num">4</span> Burn subtitles into video</h2>
              <div className="style-grid">
                <div className="field">
                  <label>Style</label>
                  <select value={subtitleStyle} onChange={e => setSubtitleStyle(e.target.value)}>
                    <option value="classic">Classic (outline)</option>
                    <option value="box">Box (semi-transparent)</option>
                    <option value="bold-box">Bold Box (opaque)</option>
                  </select>
                </div>
                <div className="field">
                  <label>Position</label>
                  <select value={subtitlePosition} onChange={e => setSubtitlePosition(e.target.value)}>
                    <option value="bottom">Bottom</option>
                    <option value="top">Top</option>
                  </select>
                </div>
                <div className="field">
                  <label>Size</label>
                  <select value={subtitleSize} onChange={e => setSubtitleSize(e.target.value)}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="field">
                  <label>Color</label>
                  <input type="color" value={subtitleColor} onChange={e => setSubtitleColor(e.target.value)} className="color-picker" />
                </div>
              </div>
              <button className="btn-primary" onClick={burnSubtitles} disabled={burning}>
                {burning ? 'Encoding video…' : 'Burn Subtitles into Video'}
              </button>
              {burning && (
                <div className="waiting-wrap">
                  <div className="spinner" />
                  <p className="waiting-msg">Encoding your video — this takes 1–2 minutes…</p>
                </div>
              )}
              {burnedPath && (
                <div className="download-box">
                  <p className="success-msg">✅ Video ready!</p>
                  {downloading ? (
                    <div className="progress-wrap">
                      <div className="progress-track"><div className="progress-bar" style={{ width: `${downloadPct}%` }} /></div>
                      <span className="progress-label">Downloading… {downloadPct}%</span>
                    </div>
                  ) : (
                    <button className="btn-primary large" onClick={downloadBurned}>⬇ Download Video with Subtitles</button>
                  )}
                </div>
              )}
            </section>
          )}

          {segments.length > 0 && <UpgradeCTA />}
          {segments.length > 0 && <RatingWidget />}

          {/* Comparison tables */}
          <div className="compare-row">

          {/* Competitor comparison */}
          <section className="card">
            <h2 className="section-title">Why AddTranscripts?</h2>
            <p className="hint" style={{ marginBottom: 20 }}>Most subtitle tools charge for the basics. We don't.</p>
            <div className="compare-table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="us">AddTranscripts</th>
                    <th>VEED.io</th>
                    <th>Kapwing</th>
                    <th>Clideo</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['100% Free',         '✅', '⚠️ Limited', '⚠️ Limited', '⚠️ Limited'],
                    ['No sign-up needed', '✅', '❌', '❌', '❌'],
                    ['No watermark',      '✅', '❌ Paid', '❌ Paid', '❌ Paid'],
                    ['Burn-in subtitles', '✅', '❌ Paid', '❌ Paid', '❌ Paid'],
                    ['Export SRT/VTT',    '✅', '✅', '✅', '✅'],
                    ['Edit transcript',   '✅', '✅', '✅', '✅'],
                    ['90+ languages',     '✅', '✅', '⚠️', '⚠️'],
                  ].map(([feat, ...vals]) => (
                    <tr key={feat}>
                      <td>{feat}</td>
                      {vals.map((v, i) => <td key={i} className={i === 0 ? 'us' : ''}>{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* AIVantage comparison */}
          <section className="card">
            <h2 className="section-title">Need more? Try AIVantage</h2>
            <p className="hint" style={{ marginBottom: 20 }}>AddTranscripts is free forever. AIVantage adds pro features for creators who need more.</p>
            <div className="compare-table-wrap">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="us">AddTranscripts</th>
                    <th>AIVantage</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['AI Transcription',        '✅', '✅'],
                    ['Edit transcript',          '✅', '✅'],
                    ['Export SRT/VTT/TXT',       '✅', '✅'],
                    ['Burn-in subtitles',        '✅', '✅'],
                    ['No watermark',             '✅', '✅'],
                    ['Translation (20+ langs)',  '❌', '✅'],
                    ['Karaoke style captions',   '❌', '✅'],
                    ['Filler word removal',      '❌', '✅'],
                    ['Silence removal',          '❌', '✅'],
                    ['Content resizing',         '❌', '✅'],
                    ['No ads',                   '❌', '✅'],
                    ['Max video length',         '10 min', '60 min'],
                  ].map(([feat, free, pro]) => (
                    <tr key={feat}>
                      <td>{feat}</td>
                      <td className="us">{free}</td>
                      <td>{pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a href="https://aivantage.tools" target="_blank" rel="noopener noreferrer" className="btn-primary large" style={{ display: 'block', textAlign: 'center', marginTop: 20, textDecoration: 'none' }}>
              Try AIVantage free →
            </a>
          </section>

          </div>{/* end compare-row */}


          {/* FAQ */}
          <section className="card">
            <h2 className="section-title">Frequently asked questions</h2>
            <div className="faq-list">
              {[
                { q: 'What does AddTranscripts do?', a: 'AddTranscripts is a free online subtitle editor. Upload any video, our AI automatically transcribes it into captions with accurate timestamps, you edit any mistakes, then download as SRT, VTT, or a video with subtitles burned in — all in under 2 minutes.' },
                { q: 'Is it really free?', a: 'Yes — completely free. No credit card, no account, no hidden limits. We show ads to keep it free for everyone.' },
                { q: 'Do I need to create an account?', a: 'No. Just upload your video and go. No sign-up, no email, nothing.' },
                { q: 'Will there be a watermark on my video?', a: 'Never. Your video is yours — no watermark, no branding added by us.' },
                { q: 'What platforms does it work for?', a: 'Any platform — TikTok, Instagram Reels, YouTube Shorts, Facebook, LinkedIn, or any video you need captions on. Export SRT/VTT for any video editor.' },
                { q: 'What video formats are supported?', a: 'MP4 and MOV files up to 500 MB and 10 minutes long. For longer videos, try AIVantage which supports up to 60 minutes.' },
                { q: 'How accurate is the transcription?', a: 'Very accurate for clear audio in any of 90+ supported languages. Background noise or strong accents may affect accuracy — you can always edit any mistakes in the transcript editor.' },
                { q: 'What subtitle formats can I export?', a: 'SRT (works with most video editors), VTT (for web/HTML5 players), and plain TXT. You can also burn subtitles directly into your video — permanently embedded, no separate file needed.' },
                { q: 'What is burn-in subtitles?', a: 'Burn-in (also called hard subtitles) permanently embeds the captions into the video itself. Viewers see them on every platform without needing to turn on captions. Ideal for social media where auto-captions are unreliable.' },
              ].map(({ q, a }) => (
                <div key={q} className="faq-item">
                  <strong>{q}</strong>
                  <p>{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* More FAQs */}
          <section className="card">
            <h2 className="section-title">More Questions Answered</h2>
            <div className="faq-list">
              {[
                { q: 'Can I add subtitles to a video in a different language?', a: 'Yes. AddTranscripts supports 90+ languages. Select your language from the dropdown before transcribing, or leave it on auto-detect. The AI will transcribe in the original spoken language. For translation into another language, try AIVantage which offers 20+ language translation.' },
                { q: 'How do I get the best transcription accuracy?', a: 'Use clear audio with minimal background noise. Speak at a normal pace. A good microphone makes a big difference. For interviews or multi-speaker recordings, accuracy is highest when one speaker talks at a time.' },
                { q: 'Can I use AddTranscripts for podcasts or audio files?', a: 'Yes — MP3, WAV, and other audio formats are supported alongside MP4 and MOV video files. Upload your podcast episode and get a full transcript with timestamps, ready to export as SRT or TXT.' },
                { q: 'What is the difference between SRT and VTT?', a: 'SRT (SubRip Text) is the most widely supported format — compatible with CapCut, Premiere Pro, DaVinci Resolve, and YouTube. VTT (WebVTT) is designed for web/HTML5 video players and online platforms. Both include the same caption text and timestamps.' },
                { q: 'Can I edit the timing of subtitles?', a: 'The transcript editor shows start and end timestamps for each segment. You can edit the text directly. For fine-grained timing adjustments, export the SRT file and edit it in a video editor like CapCut or DaVinci Resolve.' },
                { q: 'Is AddTranscripts suitable for businesses?', a: 'Yes. Businesses use AddTranscripts to add captions to training videos, product demos, webinar recordings, and social media content. No account or billing is needed, making it easy to use across teams without IT setup.' },
                { q: 'What happens if the transcription has mistakes?', a: 'Use the inline transcript editor to fix any errors. Click the Edit button on any segment, correct the text, and click Save. Your corrections are reflected in all exports — SRT, VTT, TXT, and burned video.' },
              ].map(({ q, a }) => (
                <div key={q} className="faq-item">
                  <strong>{q}</strong>
                  <p>{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Tips for better transcription */}
          <section className="card" aria-label="Tips for better transcription">
            <h2 className="section-title">Tips for Better Transcription Accuracy</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
              {[
                { icon: '🎤', title: 'Use a Good Microphone', desc: 'A decent microphone (even a phone headset) dramatically improves accuracy over built-in laptop mics. Reduce echo by recording in a small room.' },
                { icon: '🔇', title: 'Minimise Background Noise', desc: 'Record in a quiet environment. Turn off fans, air conditioning, and background music. Even low-level noise can trip up transcription.' },
                { icon: '🗣️', title: 'Speak Clearly', desc: 'Speak at a natural pace — not too fast. Enunciate clearly. Avoid trailing off at the end of sentences.' },
                { icon: '🌐', title: 'Set the Right Language', desc: 'If your video is in a specific language, select it from the dropdown instead of using auto-detect. This improves accuracy for shorter clips.' },
                { icon: '✂️', title: 'Trim Long Silences First', desc: 'Long pauses or silence at the start/end of your video can slow transcription. Trim these before uploading for faster, cleaner results.' },
                { icon: '📝', title: 'Always Review the Transcript', desc: 'AI transcription is highly accurate but not perfect. Always review the transcript editor before burning or exporting — fixing 2–3 words takes under a minute.' },
              ].map((t) => (
                <div key={t.title} style={{ background: '#fefce8', borderRadius: 12, border: '1px solid #fde68a', padding: '18px 16px' }}>
                  <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{t.icon}</div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{t.title}</h3>
                  <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Business use cases */}
          <section className="card" aria-label="Who uses AddTranscripts">
            <h2 className="section-title">Who Uses AddTranscripts?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
              {[
                { icon: '📱', title: 'Social Media Creators', desc: 'TikTok, Instagram, and YouTube creators add captions to increase views. Most social video is watched on mute — subtitles keep viewers watching.' },
                { icon: '🎙️', title: 'Podcasters', desc: 'Transcribe episodes for show notes, blog posts, and SEO. Export SRT to add captions to video podcast clips shared on social media.' },
                { icon: '🏢', title: 'Businesses & Teams', desc: 'Add captions to training videos, onboarding content, webinar recordings, and product demos. No software purchase or IT setup required.' },
                { icon: '🎓', title: 'Educators', desc: 'Make educational videos accessible with accurate captions. Export SRT files for upload to Moodle, Google Classroom, or YouTube.' },
                { icon: '📰', title: 'Journalists & Researchers', desc: 'Transcribe interviews and recordings quickly. Export as TXT for easy editing in Word or Google Docs.' },
                { icon: '♿', title: 'Accessibility Advocates', desc: 'Make video content accessible to deaf and hard-of-hearing audiences. Burned-in subtitles work everywhere without viewer settings.' },
              ].map((u) => (
                <div key={u.title} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 16px' }}>
                  <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{u.icon}</div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{u.title}</h3>
                  <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{u.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* About intro */}
          <section className="card" aria-label="About AddTranscripts">
            <h2 className="section-title">Free Online Subtitle Editor — Add Captions to Any Video</h2>
            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.8, marginTop: 12 }}>
              <strong>AddTranscripts</strong> is a free online subtitle editor that lets you add accurate AI-generated captions to any video in under two minutes. Upload your MP4 or MOV file, our AI transcribes the audio into timestamped segments, you fix any mistakes in the editor, then download the captions as an SRT or VTT file — or burn the subtitles permanently into your video. No account, no watermark, no software to install.
            </p>
            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.8, marginTop: 10 }}>
              Whether you're a TikTok creator adding captions for silent viewing, a podcaster creating a transcript, a YouTuber generating subtitles for accessibility, or a business adding captions to training videos — AddTranscripts handles it all for free. We support <strong>90+ languages</strong> with automatic language detection, so you can transcribe content in English, Spanish, Hindi, French, Arabic, Chinese, and many more.
            </p>
          </section>

          {/* Benefits */}
          <section className="card" aria-label="Why use AddTranscripts">
            <h2 className="section-title">Why Creators Choose AddTranscripts</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
              {[
                { icon: '🆓', title: 'Free Forever', desc: 'No subscription, no credit card, no hidden limits. AddTranscripts is completely free to use for everyone.' },
                { icon: '🚫', title: 'No Watermark', desc: 'Your video is yours. We never add branding or watermarks to your downloaded videos or subtitle files.' },
                { icon: '🌍', title: '90+ Languages', desc: 'Automatic language detection supports English, Spanish, French, Hindi, Arabic, Chinese, Japanese, and 80+ more.' },
                { icon: '🔥', title: 'Burn-in Subtitles', desc: 'Permanently embed captions into your video so viewers see them on every platform — no separate subtitle file needed.' },
                { icon: '👤', title: 'No Account Needed', desc: 'No sign-up, no email, no password. Just upload your video and start transcribing immediately.' },
                { icon: '📄', title: 'SRT, VTT & TXT Export', desc: 'Export your transcript as SRT for video editors, VTT for web players, or plain TXT for documents and blog posts.' },
              ].map((b) => (
                <div key={b.title} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 16px' }}>
                  <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{b.icon}</div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{b.title}</h3>
                  <p style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{b.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Platform use cases */}
          <section className="card" aria-label="Platform use cases">
            <h2 className="section-title">Add Captions for Every Platform</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
              {[
                { icon: '🎵', platform: 'TikTok', steps: ['Upload your TikTok MP4.', 'Generate AI captions in 60 seconds.', 'Edit any mistakes.', 'Burn subtitles into the video.', 'Re-upload to TikTok — captions show even on mute.'] },
                { icon: '📸', platform: 'Instagram Reels', steps: ['Upload your Reel video.', 'Get accurate timestamped captions.', 'Adjust text and timing if needed.', 'Download as MP4 with burned subtitles.', 'Post your Reel with captions for better reach.'] },
                { icon: '▶️', platform: 'YouTube Shorts', steps: ['Upload your Shorts video.', 'Auto-transcribe with 90+ language support.', 'Export as SRT for YouTube Studio.', 'Or burn-in directly for standalone sharing.', 'Boost watch time with on-screen captions.'] },
                { icon: '🎙️', platform: 'Podcasts & Audio', steps: ['Upload your podcast audio (MP3/WAV).', 'Get a full transcript with timestamps.', 'Export as TXT for show notes or blogs.', 'Export SRT to add captions to video versions.', 'Edit and clean up the transcript inline.'] },
              ].map((g) => (
                <div key={g.platform} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{g.icon} {g.platform}</h3>
                  <ol style={{ paddingLeft: 18, fontSize: '0.82rem', color: '#475569', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                </div>
              ))}
            </div>
          </section>

          {/* Device guides */}
          <section className="card" aria-label="Device guides">
            <h2 className="section-title">How to Add Subtitles on Any Device</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 16 }}>
              <div style={{ background: '#f0f9ff', borderRadius: 12, border: '1px solid #bae6fd', padding: '18px 16px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>📱 On iPhone (iOS)</h3>
                <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                  <li>Open Safari and go to <strong>addtranscripts.com</strong>.</li>
                  <li>Tap the upload area and select your video from Photos or Files.</li>
                  <li>Wait for upload and transcription to complete.</li>
                  <li>Edit any caption mistakes in the editor.</li>
                  <li>Tap <strong>Burn Subtitles</strong> and download the video to your Files app.</li>
                </ol>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: 12, border: '1px solid #86efac', padding: '18px 16px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>🤖 On Android</h3>
                <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                  <li>Open Chrome and go to <strong>addtranscripts.com</strong>.</li>
                  <li>Tap the upload area and select your video.</li>
                  <li>Tap <strong>Generate Transcript</strong> and wait 30–90 seconds.</li>
                  <li>Edit captions if needed, then tap <strong>Burn Subtitles</strong>.</li>
                  <li>Download the video — it saves to your Downloads folder.</li>
                </ol>
              </div>
              <div style={{ background: '#faf5ff', borderRadius: 12, border: '1px solid #d8b4fe', padding: '18px 16px' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>💻 On Desktop (PC/Mac)</h3>
                <ol style={{ paddingLeft: 18, fontSize: '0.85rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.6 }}>
                  <li>Go to <strong>addtranscripts.com</strong> in any browser.</li>
                  <li>Drag and drop your video file, or click to browse.</li>
                  <li>Select your language (or use auto-detect) and generate.</li>
                  <li>Edit the transcript, adjust subtitle style and position.</li>
                  <li>Download the SRT file or the video with burned-in subtitles.</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Features detailed */}
          <section className="card" aria-label="Features">
            <h2 className="section-title">Everything You Get — For Free</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 16 }}>
              {[
                { title: 'AI Transcription in 90+ Languages', desc: 'Powered by state-of-the-art speech recognition, AddTranscripts accurately transcribes spoken audio in over 90 languages. It auto-detects the language, so you don\'t need to specify it — just upload and transcribe.' },
                { title: 'Inline Transcript Editor', desc: 'After transcription, every segment is editable inline. Click Edit on any segment to fix a word, correct punctuation, or adjust timing. Changes are reflected immediately in all exports.' },
                { title: 'Burn-in (Hardcoded) Subtitles', desc: 'Permanently embed captions into your video file. Choose from Classic (outline), Box, or Bold Box styles. Set position (top or bottom) and font size. The result is a fully standalone video with visible captions on every platform.' },
                { title: 'SRT & VTT Export', desc: 'Export your transcript as an SRT file for CapCut, Premiere Pro, DaVinci Resolve, and most video editors. VTT export works with HTML5 video players and streaming platforms. Both include precise millisecond timestamps.' },
                { title: 'Karaoke Word Highlighting', desc: 'While previewing your video, the active word lights up in sync with the audio. Click any word to jump to that point in the video — making it easy to spot and fix mistakes quickly.' },
                { title: 'Privacy & Security', desc: 'Your videos are uploaded securely and deleted from our servers immediately after processing. We never store your content, never train on your data, and never share your files with third parties.' },
              ].map((f) => (
                <div key={f.title} style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 16px' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>{f.title}</h3>
                  <p style={{ fontSize: '0.83rem', color: '#475569', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact form */}
          <ContactForm />

        </main>

        </div>{/* end page-layout */}

        <footer className="footer">
          {(subCount || ratingSummary) && (
            <div className="footer-stats">
              {subCount && <span>🎬 {subCount.toLocaleString()} subtitles generated</span>}
              {subCount && ratingSummary && <span>·</span>}
              {ratingSummary && <span>⭐ {ratingSummary.avg} from {ratingSummary.count} users</span>}
            </div>
          )}
          <p>
            A free tool by{' '}
            <a href="https://aivantage.tools" target="_blank" rel="noopener noreferrer">AIVantage</a>
            {' '}· Free for personal use
          </p>
          <p style={{ marginTop: 6 }}>
            <a href="/about">About</a> · <a href="/blog">Blog</a> · <a href="/privacy">Privacy Policy</a> · <a href="/terms">Terms of Use</a>
          </p>
          <p style={{ marginTop: 6, fontSize: '0.75rem', color: '#94a3b8' }}>
            © {new Date().getFullYear()} AddTranscripts.com
          </p>
        </footer>
      </div>
    </HelmetProvider>
  );
}
